import { ActionItem, ExecReasoning, LedgerRow, Prov } from './types';

/** Ledger ids Grok Bot writes into the text: GA-21, SEC-3, CRO-2, DEC-7, M-DEL-1. */
export const LEDGER_ID = /\b(?:GA|SEC|CRO|DEC)-\d+[A-Za-z]?\b|\bM-DEL(?:-\d+)?\b/g;

export interface WhyModel {
  /** The scanned source excerpt, or why there is none. */
  excerpt: { state: 'shown' | 'withheld' | 'none'; text: string | null; lines: string | null; prov: Prov | null };
  reasoning: { text: string | null; exec: string | null; verdict: string | null; prov: Prov | null };
  /** Each linked id, with its ledger row when the snapshot has one (so it can open the Grant-asks row). */
  linked: { id: string; row: LedgerRow | null }[];
}

export function linkedIdsOf(a: ActionItem): string[] {
  const found = new Set<string>(((a.why && a.why.linked_ids) || []).map((s) => String(s)));
  const texts = [a.finding, a.for, a.text, a.recommended_action, a.bot_original, a.review && a.review.text, a.review && a.review.grant_note && a.review.grant_note.text];
  texts.forEach((t) => { if (t) (t.match(LEDGER_ID) || []).forEach((m) => found.add(m)); });
  (a.review && a.review.ga ? a.review.ga : []).forEach((g) => found.add(g.id));
  return Array.from(found);
}

export function whyOf(a: ActionItem, ledger: LedgerRow[]): WhyModel {
  const w = a.why || null;
  const status = w && w.excerpt_status;
  const excerpt: WhyModel['excerpt'] = status === 'withheld'
    ? { state: 'withheld', text: null, lines: w && w.excerpt_lines ? w.excerpt_lines : null, prov: a.prov || null }
    : w && w.excerpt
      ? { state: 'shown', text: w.excerpt, lines: w.excerpt_lines || null, prov: a.prov || null }
      : { state: 'none', text: null, lines: null, prov: a.prov || null };
  const r = a.review || null;
  // The server's reasoning is an object; a bare string is accepted too, and anything else is ignored rather than rendered.
  const raw = w ? (w.exec_reasoning as ExecReasoning | string | null | undefined) : null;
  const er: ExecReasoning | null = typeof raw === 'string' ? { text: raw } : raw && typeof raw === 'object' ? raw : null;
  const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
  const reasoning = {
    text: str(er && er.text) || str(r && r.text),
    exec: str(er && er.exec) || (r ? r.exec : null),
    verdict: str(er && er.verdict) || (r ? r.verdict : null),
    prov: (er && er.prov) || (r ? r.prov || null : null),
  };
  const linked = linkedIdsOf(a).map((id) => ({ id, row: ledger.find((l) => l.id === id) || null }));
  return { excerpt, reasoning, linked };
}
